// 2.39 and 10.20: when a Find search's best matches run low, or its replies drop (crm_search_health,
// crm_search_suggestions, crm_widen_prospect_search; migration 20261217090000). The database works out every figure;
// these helpers only put them into words for the prompt card and the per-search rates on Find › Results.
import type { HumanTask, JsonObject } from "./types";

export const SEARCH_LOW = "search_running_low";
export const SEARCH_REPLIES = "search_replies_dropped";
export const SEARCH_PROMPT_KINDS = [SEARCH_LOW, SEARCH_REPLIES];

export type WidenKind = "country" | "niche" | "regions" | "size_bands" | "rules";

/** One suggestion on the prompt: a country, a nearby niche or a filter to drop, with counts from the prospect database. */
export interface Suggestion {
  kind: WidenKind;
  value?: string | string[] | JsonObject;
  label?: string;
  relation?: "sibling" | "parent";
  firms: number;
  likely_strong?: number | null;
}

export interface ReplyBatch {
  week: string;
  sent: number;
  replied: number;
  positive: number;
}

export interface SearchPool {
  ranked: number;
  available: number;
  strong_left: number;
  picked: number;
  crossed: number;
  contacted: number;
  contacted_avg: number | null;
  picked_avg: number | null;
  next_batch_size: number;
  next_batch_avg: number | null;
  pace_per_week: number | null;
  /** "pace": the one pace from Sending rules (crm__find_pace, migration 20261227200000). */
  pace_basis: "actual" | "setting" | "cadence" | "weekly_target" | "pace";
  runway_weeks: number | null;
  recent_decided: number;
  recent_crossed: number;
}

export interface SearchHealthRow {
  search_id: string;
  title: string;
  query: string;
  status: string;
  created_at: string;
  countries: string[];
  niches: { key: string; label: string }[];
  /** Null until the search is ranked through (`ranked_at`; the Find worker ranks approved searches within minutes). */
  pool: SearchPool | null;
  pool_ready?: boolean;
  /** The newest search this one was widened into (2.39); the engine no longer prompts about this one. */
  widened_to?: string | null;
  replies: { batches: ReplyBatch[] };
  prompts: { task_id: string; kind: string; status: string; created_at: string }[];
}

export interface SearchHealth {
  settings: { pool: JsonObject; replies: { batches?: number; mature_days?: number; min_sends?: number; alpha?: number } & JsonObject };
  time_zone: string;
  searches: SearchHealthRow[];
}

/** What the engine put on the prompt task (engine/search_health.py). */
export interface PromptPayload {
  message: string;
  title?: string;
  search_id?: string;
  signals?: string[];
  suggestions?: Suggestion[];
  noun?: string;
}

export function promptOf(t: Pick<HumanTask, "payload">): PromptPayload {
  const p = (t.payload ?? {}) as JsonObject;
  return {
    message: typeof p.message === "string" ? p.message : "",
    title: typeof p.title === "string" ? p.title : undefined,
    search_id: typeof p.search_id === "string" ? p.search_id : undefined,
    signals: Array.isArray(p.signals) ? (p.signals as string[]) : [],
    suggestions: Array.isArray(p.suggestions) ? (p.suggestions as unknown as Suggestion[]) : [],
    noun: typeof p.noun === "string" ? p.noun : undefined,
  };
}

/** "FR" -> "France" (the browser's own names), the code when it has none. */
export function countryName(code: string): string {
  try {
    const n = new Intl.DisplayNames(["en-GB"], { type: "region" }).of(code.toUpperCase());
    return n && n !== code.toUpperCase() ? n : code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
}

const lowerFirst = (s: string) => (s && !/^[A-Z]{2}/.test(s) ? s.charAt(0).toLowerCase() + s.slice(1) : s);

/** The button's words: "Add France", "Include wedding venues", "Look in every region". */
export function suggestionLabel(s: Suggestion): string {
  switch (s.kind) {
    case "country":
      return `Add ${countryName(String(s.value ?? ""))}`;
    case "niche":
      return `Include ${lowerFirst(s.label ?? String(s.value ?? "a nearby niche"))}`;
    case "regions":
      return "Look in every region";
    case "size_bands":
      return "Include firms of any size";
    case "rules":
      return "Drop the rules from your crosses";
  }
}

/** The counts under the button: "about 220 likely strong matches, of 560 there". */
export function suggestionDetail(s: Suggestion, noun = "firms"): string {
  const firms = `${s.firms.toLocaleString("en-GB")} ${s.firms === 1 ? noun.replace(/s$/, "") : noun}`;
  const where = s.kind === "country" ? " there" : s.kind === "niche" ? "" : " more";
  if (typeof s.likely_strong === "number") {
    return `about ${s.likely_strong.toLocaleString("en-GB")} likely strong ${s.likely_strong === 1 ? "match" : "matches"}, of ${firms}${where}`;
  }
  return `${firms}${where}`;
}

/** The change sent to act for a suggestion (only kind, and value for a country or niche). */
export function changeOf(s: Suggestion): { kind: WidenKind; value?: string } {
  return s.kind === "country" || s.kind === "niche" ? { kind: s.kind, value: String(s.value ?? "") } : { kind: s.kind };
}

/** A one-line summary for lists that mix kinds (the Home's "Waiting on you"). */
export function promptSummary(t: Pick<HumanTask, "kind" | "payload">): string {
  const p = promptOf(t);
  const title = p.title ? `'${p.title}'` : "a search";
  return t.kind === SEARCH_REPLIES ? `Replies are dropping for ${title}` : `Strong matches running low in ${title}`;
}

export interface Rates {
  sent: number;
  replied: number;
  positive: number;
  reply_rate: number | null;
  positive_rate: number | null;
}

function rates(rows: ReplyBatch[]): Rates {
  const sent = rows.reduce((n, b) => n + b.sent, 0);
  const replied = rows.reduce((n, b) => n + b.replied, 0);
  const positive = rows.reduce((n, b) => n + b.positive, 0);
  return { sent, replied, positive, reply_rate: sent ? replied / sent : null, positive_rate: sent ? positive / sent : null };
}

/** All batches, the last `n` and those before: what 10.20 compares. */
export function searchRates(row: SearchHealthRow, n = 3): { all: Rates; recent: Rates; before: Rates; batches: number } {
  const b = [...(row.replies?.batches ?? [])].sort((x, y) => x.week.localeCompare(y.week));
  return { all: rates(b), recent: rates(b.slice(-n)), before: rates(b.slice(0, Math.max(0, b.length - n))), batches: b.length };
}

/** 0.09 -> "9%", 0.025 -> "2.5%", null -> "–". */
export function pctText(r: number | null): string {
  if (r === null) return "–";
  const v = Math.round(r * 1000) / 10;
  return `${Number.isInteger(v) ? v.toFixed(0) : v.toFixed(1)}%`;
}

/** "about 2 weeks at your pace (20 a week)" for the pool line on the card. */
export function runwayText(p: SearchPool): string {
  if (p.runway_weeks === null || p.pace_per_week === null) return "";
  const w = p.runway_weeks < 1 ? "less than a week" : `about ${Math.round(p.runway_weeks)} ${Math.round(p.runway_weeks) === 1 ? "week" : "weeks"}`;
  const basis =
    p.pace_basis === "actual"
      ? "at your pace"
      : p.pace_basis === "cadence"
        ? "at your cadence plan"
        : p.pace_basis === "setting"
          ? "at your planned pace"
          : p.pace_basis === "pace"
            ? "at your pace in Sending rules"
            : "at the default weekly target";
  const pace = p.pace_per_week < 1 ? "under 1" : String(Math.round(p.pace_per_week));
  return `${w} ${basis} (${pace} a week)`;
}

// ------------------------------------------------------------------ Settings › Sending › Mailbox and search health, search depth (L-12, item 89)

/** How deep the business's searches still are, over every search it still works from (not one widened into another). */
export interface SearchDepth {
  /** Searches ranked and still in use. */
  searches: number;
  /** Searches approved but not ranked through yet. */
  waiting: number;
  /** Good-fit (strong) firms left to contact across them; a firm in two searches counts in each. */
  strongLeft: number;
  /** New firms a week: what was actually contacted in 28 days across the searches, else the planned pace. */
  pace: number | null;
  basis: SearchPool["pace_basis"] | null;
  weeks: number | null;
}

/** The searches still in use, ranked, newest first. */
export function liveSearches(h: SearchHealth): SearchHealthRow[] {
  return h.searches.filter((s) => !s.widened_to && s.pool);
}

/**
 * `pace`: the one pace (lib/pace.ts computePace, new firms a week from Sending rules). When nothing was contacted in 28
 * days it is the planned pace, so Health and Sending rules agree; without it, the searches' own planned pace.
 */
export function searchDepth(h: SearchHealth, onePace?: number | null): SearchDepth {
  const live = liveSearches(h);
  const waiting = h.searches.filter((s) => !s.widened_to && !s.pool).length;
  const strongLeft = live.reduce((n, s) => n + (s.pool?.strong_left ?? 0), 0);
  // Actual paces are per search and add up; a planned pace is the business's own, the same on every search.
  const actual = live.filter((s) => s.pool?.pace_basis === "actual" && (s.pool?.pace_per_week ?? 0) > 0);
  let pace: number | null = null;
  let basis: SearchDepth["basis"] = null;
  if (actual.length) {
    pace = actual.reduce((n, s) => n + (s.pool?.pace_per_week ?? 0), 0);
    basis = "actual";
  } else if (onePace && onePace > 0) {
    pace = onePace;
    basis = "pace";
  } else {
    const planned = live.map((s) => s.pool!).filter((p) => p.pace_basis !== "actual" && (p.pace_per_week ?? 0) > 0);
    const top = planned.sort((a, b) => (b.pace_per_week ?? 0) - (a.pace_per_week ?? 0))[0];
    if (top) {
      pace = top.pace_per_week;
      basis = top.pace_basis;
    }
  }
  const weeks = pace && pace > 0 ? Math.round((strongLeft / pace) * 10) / 10 : null;
  return { searches: live.length, waiting, strongLeft, pace, basis, weeks };
}

/** "about 9 weeks", "less than a week". */
export function weeksText(weeks: number): string {
  if (weeks < 1) return "less than a week";
  const w = Math.round(weeks);
  return `about ${w} ${w === 1 ? "week" : "weeks"}`;
}

/** The card's subtitle: "About 9 weeks left at your pace · 214 good-fit firms". */
export function depthLine(d: SearchDepth): string {
  if (!d.searches) return d.waiting ? "Your search is being ranked" : "No search yet";
  const firms = `${d.strongLeft.toLocaleString("en-GB")} good-fit ${d.strongLeft === 1 ? "firm" : "firms"}`;
  if (d.weeks === null) return `${firms} left`;
  const w = weeksText(d.weeks);
  return `${w.charAt(0).toUpperCase()}${w.slice(1)} left at your pace · ${firms}`;
}

/** The pace in words: "12 new firms a week, what you contacted over the last 4 weeks". */
export function paceText(d: SearchDepth): string {
  if (d.pace === null) return "";
  const n = d.pace < 1 ? "under 1" : String(Math.round(d.pace));
  const why =
    d.basis === "actual" ? "what you contacted over the last 4 weeks" : d.basis === "cadence" ? "your cadence plan" : d.basis === "setting" ? "your planned pace" : d.basis === "pace" ? "your pace in Sending rules" : "the default weekly target";
  return `${n} new ${d.pace === 1 ? "firm" : "firms"} a week, ${why}`;
}

/** An adjacent niche or country the prospect database has, with the search it would widen. */
export interface NearbyOption extends Suggestion {
  search_id: string;
  search_title: string;
}

/** What crm_search_suggestions returns (its filters are left to the search's own prompt). */
export interface SearchSuggestions {
  search_id: string;
  countries: Suggestion[];
  niches: Suggestion[];
}

/**
 * Nearby niches and countries across the searches, most firms first, each once (the search with most firms there
 * keeps it), at most `max`.
 */
export function nearbyOptions(lists: SearchSuggestions[], titles: Record<string, string>, max = 6): NearbyOption[] {
  const best = new Map<string, NearbyOption>();
  for (const l of lists) {
    for (const s of [...(l.niches ?? []), ...(l.countries ?? [])]) {
      if (!s || (s.kind !== "niche" && s.kind !== "country") || !(s.firms > 0)) continue;
      const key = `${s.kind}:${String(s.value ?? "")}`;
      const had = best.get(key);
      if (!had || s.firms > had.firms) best.set(key, { ...s, search_id: l.search_id, search_title: titles[l.search_id] ?? "your search" });
    }
  }
  return [...best.values()].sort((a, b) => b.firms - a.firms || suggestionLabel(a).localeCompare(suggestionLabel(b))).slice(0, max);
}

/** What "Ask Ogma to load more" sends to Ogma's team (act suggest_source, the Data sources request path). */
export function loadMoreRequest(d: SearchDepth, titles: string[]): { what: string; why: string } {
  const named = titles.slice(0, 2).map((t) => `'${t}'`).join(" and ");
  const what = `Load more firms like ${named || "my searches"}`.slice(0, 200);
  const left = `${d.strongLeft.toLocaleString("en-GB")} good-fit firms left`;
  const why = d.weeks === null ? `${left}.` : `${left}, ${weeksText(d.weeks)} at ${Math.round(d.pace ?? 0)} a week.`;
  return { what, why };
}
